import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate, useParams} from 'react-router-dom';
import {Box, Button, Stack} from '@mui/material';
import {useSnackbar} from 'notistack';
import {useObservable} from '@ngneat/react-rxjs';
import {PictureAsPdfOutlined} from '@mui/icons-material';

import {JobInvoiceStatus, jobsInvoiceQuery, jobsInvoiceService} from '@store/invoices/jobsInvoice';

import {Download, Zip} from '@icons/index';

import ModalComponent from '@components/modal/Modal.component';
import {Invoice, invoicesService} from '@store/invoices/invoices';
import {SelectItem} from '@components/inputs/select/Select.component';
import ConfirmJobInvoiceValidationModal
  from '@screens/admin/invoices/invoicesValidation/details/ConfirmJobInvoiceValidation.modal';
import JobInvoiceFiltersComponent
  from '@screens/admin/invoices/invoicesValidation/details/filters/JobInvoiceFilters.component';
import NewTableComponent from '@components/table/NewTable.component';
import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {GridValueGetterParams} from '@mui/x-data-grid-pro';
import {OtherActionType} from '@utils/table/columnTypes/actions.columnType';
import {NumberExt} from '@utils/extensions/number.extension';
import CheckboxComponent from '@components/inputs/checkbox/Checkbox.component';

export interface JobInvoiceDetailsFilters {
  clients?: SelectItem[];
  clientUnits?: SelectItem[];
  clientTypologies?: SelectItem[];
  minPrice?: number;
  maxPrice?: number;
}

const JobInvoiceDetailsModal = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {jobInvoiceId} = useParams();
  const {enqueueSnackbar} = useSnackbar();

  const [{invoices, loading: invoicesLoading}] = useObservable(jobsInvoiceQuery.invoices$);
  const [selectedInvoices] = useObservable(jobsInvoiceQuery.selectedInvoices$);

  const [jobInvoiceStatus, setJobInvoiceStatus] = useState<JobInvoiceStatus>();
  const [jobInvoicePeriod, setJobInvoicePeriod] = useState('');

  const [filters, setFilters] = useState<JobInvoiceDetailsFilters>({});
  const [showDeletedInvoices, setShowDeletedInvoices] = useState<boolean>(false);

  const [showConfirm, setShowConfirm] = useState(false);

  const handleClose = () => {
    jobsInvoiceService.resetJobInvoiceDetails();
    navigate('..', {replace: true});
  };

  useEffect(() => {
    if (jobInvoiceId) {
      jobsInvoiceService.getJobInvoiceDetails(jobInvoiceId).subscribe({
        next: (ji) => {
          setJobInvoiceStatus(ji.status);
          if (ji.from && ji.to)
            setJobInvoicePeriod(t('global.period', {from: new Date(ji.from), to: new Date(ji.to)}));
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }
  }, [enqueueSnackbar, jobInvoiceId, t]);

  const handleDownloadCSV = () => {
    if (!jobInvoiceId) return null;
    jobsInvoiceService.csvJobInvoice(jobInvoiceId).subscribe({
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  };

  const handleDownloadZIP = () => {
    if (!jobInvoiceId) return null;
    jobsInvoiceService.zipJobInvoice(jobInvoiceId).subscribe({
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  };

  const handleDownloadPDF = () => {
    if (!jobInvoiceId) return null;
    jobsInvoiceService.printJobInvoice(jobInvoiceId).subscribe({
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  };

  return (
    <>
      <ModalComponent
        maxWidth={false}
        handleClose={handleClose}
        title={t('invoices.invoicesValidation.details.title')}
        subTitle={`${t('invoices.invoicesValidation.table.period')} ${jobInvoicePeriod.toLowerCase()}`}
        content={(
          <Stack
            pt="30px" pb={jobInvoiceStatus !== JobInvoiceStatus.TO_BE_VALIDATED ? '25px' : undefined}
            height="65vh" width="100%"
            spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              {jobInvoiceStatus !== JobInvoiceStatus.TO_BE_VALIDATED && <Box>
                  <CheckboxComponent
                      handleChange={setShowDeletedInvoices}
                      value={showDeletedInvoices}
                      label={t('invoices.invoicesValidation.table.filterDeletedInvoices')}/>
              </Box>}
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end">

                <JobInvoiceFiltersComponent
                  filters={filters}
                  handleSave={setFilters}
                  handleReset={() => setFilters({})}/>
              </Stack>
            </Stack>
            <NewTableComponent
              loading={invoicesLoading}
              columns={[
                COLUMN_TYPES.TEXT({
                  cellClassName: (params: GridValueGetterParams<any, Invoice>) => params.row.deletedAt != null || (jobInvoiceStatus === JobInvoiceStatus.TO_BE_VALIDATED && !selectedInvoices.some((si) => si === params.row.id)) ? 'bold lineThrough' : 'bold',
                  field: 'invoiceRef',
                  label: t('invoices.invoicesValidation.details.table.bill'),
                  width: 250,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'client',
                  color: 'secondary',
                  label: t('invoices.invoicesValidation.details.table.client'),
                  valueGetter: (params: GridValueGetterParams<any, Invoice>) => params.row.clientCount > 1 ? t('global.multiple', {count: params.row.clientCount}) : params.row.clientName,
                  fullWidth: true,
                  minWidth: 350,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'clientSite',
                  color: 'secondary',
                  label: t('invoices.invoicesValidation.details.table.clientSite'),
                  valueGetter: (params: GridValueGetterParams<any, Invoice>) => params.row.unitCount > 1 ? t('global.multiple', {count: params.row.unitCount}) : params.row.unitName,
                  fullWidth: true,
                  minWidth: 350,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'organisationKind',
                  color: 'secondary',
                  label: t('invoices.invoicesValidation.details.table.typology'),
                  width: 200,
                }),
                COLUMN_TYPES.INPUT({
                  field: 'comment',
                  label: t('invoices.invoicesValidation.details.table.comment'),
                  fullWidth: true,
                  minWidth: 350,
                }),
                COLUMN_TYPES.TEXT({
                  field: 'invoice.totalAmountNoVatInCents',
                  label: t('invoices.invoicesValidation.details.table.amount'),
                  valueGetter: (params: GridValueGetterParams<any, Invoice>) => {
                    const price = params.row.totalAmountNoVatInCents;
                    return price !== undefined ? `${NumberExt.round(price / 100, 2)} ${params.row.currency}` : '-';
                  },
                  width: 150,
                }),
                COLUMN_TYPES.ACTIONS({
                  handleShowRow: (invoice: Invoice) => navigate(`${invoice.id}`),
                  otherActions: [
                    {
                      type: OtherActionType.PDF,
                      handleClick: (invoice: Invoice) => {
                        invoicesService.printInvoice(invoice.id).subscribe({
                          error: (err) => enqueueSnackbar(err.text, err.options),
                        });
                      },
                    },
                  ],
                }),
              ]}
              disablePagination
              disableRowSelection={jobInvoiceStatus !== JobInvoiceStatus.TO_BE_VALIDATED}
              handleRowSelected={jobsInvoiceService.setSelectedInvoices}
              rowsSelected={jobInvoiceStatus !== JobInvoiceStatus.TO_BE_VALIDATED ? [] : selectedInvoices}
              data={invoices.filter((i) => {
                if (!!filters.clients?.length && !filters.clients.some((c) => c.label === i.clientName)) return false;
                if (!!filters.clientUnits?.length && !filters.clientUnits.some((c) => c.label === i.unitName)) return false;
                if (!!filters.clientTypologies?.length && !filters.clientTypologies.some((c) => c.label === i.organisationKind)) return false;
                if (filters.minPrice !== undefined && i.totalAmountNoVatInCents / 100 < filters.minPrice) return false;
                if (filters.maxPrice !== undefined && i.totalAmountNoVatInCents / 100 > filters.maxPrice) return false;
                if (!showDeletedInvoices && i.deletedAt != null) return false;
                return true;
              })}/>
            <Stack direction="row" justifyContent="flex-end" spacing={2}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleDownloadPDF}
                startIcon={<PictureAsPdfOutlined style={{color: '#172134', transform: 'scale(1.1)'}}/>}>
                {t('invoices.invoicesValidation.details.download')}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleDownloadZIP}
                startIcon={<Zip style={{color: '#172134', transform: 'scale(0.9)'}}/>}>
                {t('invoices.invoicesValidation.details.zip')}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleDownloadCSV}
                startIcon={<Download/>}>
                {t('invoices.invoicesValidation.details.csv')}
              </Button>
            </Stack>
          </Stack>
        )}
        actions={jobInvoiceStatus === JobInvoiceStatus.TO_BE_VALIDATED ? (
          <>
            <Button variant="outlined" color="primary" onClick={handleClose} disabled={invoicesLoading}>
              {t('global.cancel')}
            </Button>
            <Button variant="contained" color="primary" disabled={invoicesLoading} onClick={() => setShowConfirm(true)}>
              {t('global.validate')}
            </Button>
          </>
        ) : undefined}/>
      {showConfirm && (
        <ConfirmJobInvoiceValidationModal
          handleClose={() => setShowConfirm(false)}
          handleSuccess={handleClose}/>
      )}
    </>
  );
};

export default JobInvoiceDetailsModal;
